import { config } from '@terminal/config';

import { PDF_FONT_FAMILY } from '../../alfred/research/utils/htmlNodes';

import { prepareClipboardHtml } from './prepareClipboardHtml';
import { withExpandedDrawerAccordions } from './prepareDrawerContent';
import { rasterizeSrcToPng } from './rasterizeClipboardImage';

const BRAND_NAME_PATH = '/app/static/images/brand/brand-name-dark.svg';
const HEADER_LOGO_WIDTH_PX = 103;
const HEADER_LOGO_MARGIN_BOTTOM_PX = 24;

interface HandleCopyDrawerArgs {
  contentToCopy: HTMLDivElement;
  expandAccordions?: (expand: boolean) => void;
}

const buildBrandHeaderHtml = async (): Promise<string> => {
  const png = await rasterizeSrcToPng({
    src: `${config.urls.root}${BRAND_NAME_PATH}`,
    width: HEADER_LOGO_WIDTH_PX,
  });
  if (!png) {
    return '';
  }

  return `<div style="margin-bottom:${HEADER_LOGO_MARGIN_BOTTOM_PX}px;"><img src="${png}" alt="Company logo" width="${HEADER_LOGO_WIDTH_PX}" style="width:${HEADER_LOGO_WIDTH_PX}px;height:auto;" /></div>`;
};

const writeClipboard = ({
  html,
  plainContent,
  toPng,
}: {
  html: string;
  plainContent: string;
  toPng: () => Promise<string | undefined>;
}) => {
  const clipboardDocument = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
      </head>
      <body style="font-family:${PDF_FONT_FAMILY};background:#ffffff;">
        ${html}
      </body>
    </html>
  `;

  return navigator.clipboard
    .write([
      new ClipboardItem({
        'text/plain': new Blob([plainContent.trim()], { type: 'text/plain' }),
        'text/html': new Blob([clipboardDocument], { type: 'text/html' }),
      }),
    ])
    .catch(() =>
      toPng()
        .then(pngDataUrl => {
          if (!pngDataUrl) {
            throw new Error('Clipboard write failed');
          }
          return fetch(pngDataUrl);
        })
        .then(response => response.blob())
        .then(pngBlob =>
          navigator.clipboard.write([
            new ClipboardItem({ 'image/png': pngBlob }),
          ])
        )
    );
};

export const handleCopyDrawer = ({
  contentToCopy,
  expandAccordions,
}: HandleCopyDrawerArgs): Promise<void> => {
  const previousOpacity = contentToCopy.style.opacity;
  const shouldHideLiveContent = Boolean(expandAccordions);
  // visibility:hidden zeros innerText and is inherited into snapshot PNGs.
  if (shouldHideLiveContent) {
    contentToCopy.style.opacity = '0';
  }

  let disposePrepared: (() => void) | undefined;

  return withExpandedDrawerAccordions({
    expandAccordions,
    run: () => {
      const plainContent = contentToCopy.innerText;
      return buildBrandHeaderHtml()
        .then(brandHeaderHtml =>
          prepareClipboardHtml(contentToCopy, brandHeaderHtml)
        )
        .then(prepared => {
          disposePrepared = prepared.dispose;
          return writeClipboard({
            html: prepared.html,
            plainContent,
            toPng: prepared.toPng,
          });
        });
    },
  }).finally(() => {
    disposePrepared?.();
    if (shouldHideLiveContent) {
      contentToCopy.style.opacity = previousOpacity;
    }
  });
};
